import {
  customElement,
  DeesElement,
  property,
  state,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';
import { renderMarkdown } from './dotglobal-markdown.js';
import type * as interfaces from '../../ts/ffb.interfaces.js';

/**
 * Releases tab: expandable release cards (Latest/Pre-release badges, author, age, asset
 * download rows incl. source archives, Watch + Atom-feed actions). Mirrors the Claude
 * Design `releases.jsx`, wired to the real `IPublicRelease[]`. Extracted out of
 * dotglobal-app to keep view components small. Emits `dotglobal-navigate`,
 * `dotglobal-toast`, and `dotglobal-copy`.
 */
@customElement('dotglobal-releases')
export class DotglobalReleases extends DeesElement {
  @property({ type: Object })
  public accessor repo: interfaces.IPublicRepo | null = null;

  @property({ type: Array })
  public accessor releases: interfaces.IPublicRelease[] = [];

  @state()
  private accessor openMap: Record<string, boolean> = {};

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; }
      .relcard { background: var(--bg-primary); }
      .relhead {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 11px 16px;
        cursor: pointer;
        font-size: 13.5px;
        transition: background 0.12s;
      }
      .relhead:hover { background: var(--row-hover); }
      .relcard.open > .relhead {
        border-bottom: 1px solid var(--border-default);
        background: var(--bg-tertiary);
      }
      .relhead .chev { color: var(--text-muted); flex-shrink: 0; display: inline-flex; }
      .relhead .reltag { display: inline-flex; flex-shrink: 0; }
      .relver {
        font-weight: 650;
        font-size: 14px;
        font-family: var(--font-mono);
        white-space: nowrap;
      }
      .relti {
        color: var(--text-muted);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
      }
      .relmeta {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: var(--text-muted);
        font-size: 12px;
      }
      .relwho { background: none; border: 0; cursor: pointer; }
      .relwho:hover { color: var(--text-primary); }
      .relwho .avatar { width: 18px; height: 18px; font-size: 8px; }
      .relbody { padding: 18px 20px; }
      .relassets {
        margin-top: 18px;
        border: 1px solid var(--border-default);
        border-radius: 6px;
        overflow: hidden;
      }
      .ra-h {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 38px;
        padding: 0 14px;
        font-size: 12.5px;
        font-weight: 600;
        background: var(--bg-tertiary);
        border-bottom: 1px solid var(--border-default);
      }
      .ra-h .cnt {
        background: var(--bg-tertiary);
        color: var(--text-secondary);
        border: 1px solid var(--border-default);
        border-radius: 999px;
        padding: 1px 7px;
        font-size: 11px;
      }
      .asset-row {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 8px 14px;
        border-bottom: 1px solid var(--border-subtle);
        font-size: 13px;
        cursor: pointer;
        transition: background 0.12s;
        color: inherit;
      }
      .asset-row:last-child { border-bottom: none; }
      .asset-row:hover { background: var(--row-hover); }
      .asset-row .ai { color: var(--text-muted); display: inline-flex; flex-shrink: 0; }
      .asset-row .an {
        font-family: var(--font-mono);
        font-size: 12.5px;
        color: var(--accent);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .asset-row:hover .an { text-decoration: underline; }
      .asset-row .dlb { color: var(--text-muted); display: inline-flex; }
      .asset-row:hover .dlb { color: var(--text-primary); }
      .empty { text-align: center; padding: 60px 20px; color: var(--text-muted); }
    `,
  ];

  public render(): TemplateResult {
    const repo = this.repo;
    if (!repo) {
      return html`<div class="page"><div class="empty">Repository not found.</div></div>`;
    }
    const releases = this.releases;
    const total = repo.releaseCount || releases.length;
    return html`
      <div class="page">
        <div class="row wrap" style="justify-content: space-between; gap: 10px; margin-bottom: 16px">
          <div class="row" style="gap: 10px">
            <span style="font-weight: 600; font-size: 16px">Releases</span>
            <span class="badge def sq">${total}</span>
          </div>
          <div class="row" style="gap: 8px">
            <button class="btn sm ghost" @click=${() => this.toast('Subscribed to release notifications')}>${this.icon('Eye')} Watch releases</button>
            <button class="btn sm ghost" @click=${() => this.copy(`https://foss.global/${repo.org}/${repo.name}/releases.atom`)}>${this.icon('Copy')} Atom feed</button>
          </div>
        </div>
        ${releases.length
          ? html`
              <div class="stack" style="gap: 12px">
                ${releases.map((release, index) => this.renderReleaseCard(repo, release, index === 0))}
              </div>
              <div class="row" style="gap: 10px; justify-content: center; margin-top: 20px">
                <span class="muted" style="font-size: 12.5px">Showing ${releases.length} of ${total} release${total === 1 ? '' : 's'}</span>
              </div>
            `
          : html`<div class="card"><div class="empty">No releases published yet.</div></div>`}
      </div>
    `;
  }

  private renderReleaseCard(repoArg: interfaces.IPublicRepo, releaseArg: interfaces.IPublicRelease, isLatestArg: boolean): TemplateResult {
    const open = this.openMap[releaseArg.tagName] ?? isLatestArg;
    const assetCount = releaseArg.assets.length + 2;
    return html`
      <div class=${`card relcard ${open ? 'open' : ''}`}>
        <div class="relhead" @click=${() => this.toggle(releaseArg.tagName, isLatestArg)}>
          <span class="chev">${this.icon(open ? 'ChevronDown' : 'ChevronRight')}</span>
          <span class="reltag" style=${`color:${isLatestArg ? 'var(--success)' : 'var(--text-muted)'}`}>${this.icon('Tag')}</span>
          <span class="relver">${releaseArg.tagName}</span>
          ${isLatestArg ? html`<span class="badge success sq">Latest</span>` : ''}
          ${releaseArg.prerelease ? html`<span class="badge warning sq">Pre-release</span>` : ''}
          ${releaseArg.name && releaseArg.name !== releaseArg.tagName ? html`<span class="relti">${releaseArg.name}</span>` : ''}
          <span class="grow"></span>
          ${!open ? html`<span class="relmeta nowrap">${assetCount} assets</span>` : ''}
          <button
            class="relmeta relwho nowrap"
            @click=${(eventArg: Event) => { eventArg.stopPropagation(); if (releaseArg.author.login && releaseArg.author.htmlUrl) { this.navigate(`#/user/${releaseArg.author.login}`); } }}
          >
            ${this.renderAvatar(releaseArg.author.login || 'unknown', releaseArg.author.avatarUrl)} ${releaseArg.author.login || 'unknown'}
          </button>
          <span class="relmeta nowrap">${this.timeAgo(releaseArg.publishedAt)}</span>
        </div>
        ${open
          ? html`
              <div class="relbody">
                ${releaseArg.body
                  ? html`<div class="md">${renderMarkdown(releaseArg.body, 200)}</div>`
                  : html`<div class="muted" style="font-size: 13px">No release notes provided.</div>`}
                <div class="relassets">
                  <div class="ra-h">${this.icon('Package')} Assets <span class="cnt">${assetCount}</span></div>
                  ${releaseArg.assets.map((asset) => this.renderAssetRow(asset.name, this.formatBytes(asset.size), asset.downloadCount, asset.browserDownloadUrl, false))}
                  ${this.renderAssetRow('Source code (zip)', '', null, `${repoArg.htmlUrl}/archive/${releaseArg.tagName}.zip`, true)}
                  ${this.renderAssetRow('Source code (tar.gz)', '', null, `${repoArg.htmlUrl}/archive/${releaseArg.tagName}.tar.gz`, true)}
                </div>
              </div>
            `
          : ''}
      </div>
    `;
  }

  private renderAssetRow(nameArg: string, sizeArg: string, downloadsArg: number | null, urlArg: string, sourceArg: boolean): TemplateResult {
    return html`
      <a class="asset-row" href=${urlArg} target="_blank" rel="noopener">
        <span class="ai">${this.icon(sourceArg ? 'FileCode' : 'Package')}</span>
        <span class="an">${nameArg}</span>
        <span class="grow"></span>
        ${downloadsArg != null && downloadsArg > 0 ? html`<span class="relmeta">${downloadsArg.toLocaleString()} downloads</span>` : ''}
        ${sizeArg ? html`<span class="relmeta" style="min-width: 58px; text-align: right">${sizeArg}</span>` : ''}
        <span class="dlb">${this.icon('Download')}</span>
      </a>
    `;
  }

  private toggle(tagArg: string, defaultOpenArg: boolean): void {
    const current = this.openMap[tagArg] ?? defaultOpenArg;
    this.openMap = { ...this.openMap, [tagArg]: !current };
  }

  private navigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', { detail: { hash: hashArg }, bubbles: true, composed: true }));
  }

  private toast(messageArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-toast', { detail: { message: messageArg }, bubbles: true, composed: true }));
  }

  private copy(textArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-copy', { detail: { text: textArg }, bubbles: true, composed: true }));
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${14}></dees-icon>`;
  }

  private renderAvatar(labelArg: string, avatarUrlArg: string): TemplateResult {
    const initials = labelArg.split(/[.\s/-]+/).filter(Boolean).slice(0, 2).map((partArg) => partArg[0]?.toUpperCase()).join('') || 'fg';
    return html`
      <span class="avatar">
        ${initials}
        ${avatarUrlArg
          ? html`<img src=${avatarUrlArg} alt="" loading="lazy" referrerpolicy="no-referrer" @error=${(eventArg: Event) => { (eventArg.currentTarget as HTMLImageElement).style.display = 'none'; }} />`
          : ''}
      </span>
    `;
  }

  private timeAgo(timestampArg: string): string {
    if (!timestampArg) {
      return 'unknown';
    }
    const diffMs = Date.now() - new Date(timestampArg).getTime();
    if (!Number.isFinite(diffMs)) {
      return 'unknown';
    }
    const minutes = Math.max(1, Math.round(diffMs / 60000));
    if (minutes < 60) {
      return `${minutes}m ago`;
    }
    const hours = Math.round(minutes / 60);
    if (hours < 48) {
      return `${hours}h ago`;
    }
    const days = Math.round(hours / 24);
    if (days < 60) {
      return `${days}d ago`;
    }
    return `${Math.round(days / 30)}mo ago`;
  }

  private formatBytes(bytesArg: number): string {
    if (bytesArg < 1024) {
      return `${bytesArg} B`;
    }
    if (bytesArg < 1024 * 1024) {
      return `${Math.round(bytesArg / 1024)} KB`;
    }
    return `${(bytesArg / (1024 * 1024)).toFixed(1)} MB`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-releases': DotglobalReleases;
  }
}
